// Semantic ("Similar tenders") search — everything non-visual.
//
// The tender Lambda's GET /tenders/search embeds the typed term (Cohere Embed
// v4) and returns the nearest tenders as ids + cosine similarity. This module
// decides when a term warrants that request and joins the ranked ids to the
// tenders the page already holds; TenderQPage only renders.

import { vendorLine } from './tenderAward';
import type { AwardVendor } from './tenderAward';

export interface SemanticHit {
    tenderId: string;
    similarity: number;
}

export interface SimilarTender<T> {
    tender: T;
    similarity: number;
}

export const SEMANTIC_MIN_CHARS = 3;
export const SEMANTIC_MAX_RESULTS = 20;

// Bare numbers ("39911", "#39911") are project-number lookups — the exact
// client-side match handles them; no Bedrock call.
export function isSemanticTerm(term: string): boolean {
    const t = term.trim();
    return t.length >= SEMANTIC_MIN_CHARS && !/^#?\d+$/.test(t);
}

// Backend order is kept (it is the ranking we paid for). Exact matches are
// never repeated, ids the page does not hold are dropped.
export function similarTenders<T extends { id: string }>(
    hits: SemanticHit[],
    candidates: T[],
    exactIds: Set<string>,
    max: number = SEMANTIC_MAX_RESULTS,
): SimilarTender<T>[] {
    const byId = new Map(candidates.map(c => [c.id, c] as const));
    const seen = new Set<string>();
    const out: SimilarTender<T>[] = [];
    for (const hit of hits) {
        if (out.length >= max) break;
        if (exactIds.has(hit.tenderId) || seen.has(hit.tenderId)) continue;
        const tender = byId.get(hit.tenderId);
        if (!tender) continue;
        seen.add(hit.tenderId);
        out.push({ tender, similarity: hit.similarity });
    }
    return out;
}

// Worded so it cannot be mistaken for the coloured fit-score percentage.
export function similarityLabel(similarity: number): string {
    const pct = Math.round(Math.max(0, Math.min(1, similarity)) * 100);
    return `${pct}% similar`;
}

// --- Row status line ----------------------------------------------------------
// Every row states its deadline (due / closed / none) and whether documents
// exist, computed from the same predicate the "Open for Submission" filter
// uses — so a search result that the filters would normally hide explains
// itself, and the status line is never empty.

export type RowStatusKind =
    | 'due' | 'closed' | 'no-deadline' | 'no-documents' | 'nda' | 'nda-required'
    | 'unprocessed' | 'unscored' | 'abandoned' | 'awarded';

// SIMAP publishes an abandonment or an award as a further publication of the
// project; the backend passes the newest one's type as LatestPubType. Either
// means the tender is concluded, whatever its deadline says.
export function isConcluded(latestPubType: string | null | undefined): boolean {
    if (!latestPubType) return false;
    return latestPubType === 'abandonment' || /award/i.test(latestPubType);
}

export interface RowStatusInput {
    tenderStart?: string | null;
    hasDocuments: boolean;
    processed?: boolean;              // a summary exists (the pipeline finished)
    matchingScore?: number | null;
    latestPubType?: string | null;
    ndaGated?: boolean;               // only a non-disclosure agreement was published (tag docs:nda)
    ndaRequired?: boolean;            // the documents came in after an NDA was signed (tag docs:nda-required)
    awardVendors?: AwardVendor[] | null; // who won, from SIMAP's award notice
}

export interface RowStatusMarker {
    kind: RowStatusKind;
    label: string;
    tooltip: string;
}

// "NDA" filter: 'only' lists the tenders still to sign (documents behind the
// NDA), 'required' every tender that needs one (signed or not), 'hide' drops
// the unsigned ones — their score is capped — and keeps the signed ones.
export type NdaFilter = 'all' | 'only' | 'required' | 'hide';

export function passesNdaFilter(filter: NdaFilter, tender: { ndaGated?: boolean; ndaRequired?: boolean }): boolean {
    switch (filter) {
        case 'only': return !!tender.ndaGated;
        case 'required': return !!tender.ndaGated || !!tender.ndaRequired;
        case 'hide': return !tender.ndaGated;
        default: return true;
    }
}

export function startOfToday(now: Date = new Date()): Date {
    const day = new Date(now);
    day.setHours(0, 0, 0, 0);
    return day;
}

// "Open for Submission": a proposal can still be handed in when the
// submission deadline (TenderStart = the SIMAP offer deadline) is today or
// later. A missing or unreadable deadline (uploaded RFPs) counts as open.
export function canStillSubmit(tenderStart: string | null | undefined, today: Date): boolean {
    if (!tenderStart) return true;
    const deadline = new Date(tenderStart);
    return isNaN(deadline.getTime()) || deadline >= today;
}

const SHORT_DATE: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
const FULL_DATE: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };

function parseDeadline(tenderStart: string | null | undefined): Date | null {
    if (!tenderStart) return null;
    const deadline = new Date(tenderStart);
    return isNaN(deadline.getTime()) ? null : deadline;
}

export function rowStatus(tender: RowStatusInput, today: Date): RowStatusMarker[] {
    const markers: RowStatusMarker[] = [];
    if (isConcluded(tender.latestPubType)) {
        // Nothing else will happen to a concluded tender: the deadline and
        // the processing state no longer matter.
        return tender.latestPubType === 'abandonment'
            ? [{ kind: 'abandoned', label: 'Abandoned',
                 tooltip: 'SIMAP published an abandonment for this project — the tender is closed.' }]
            : [{ kind: 'awarded', label: 'Awarded',
                 tooltip: 'SIMAP published the award for this project — the tender is closed.'
                     + ((tender.awardVendors ?? []).length > 0
                         ? ` Awarded to ${tender.awardVendors!.map(vendorLine).join('; ')}.` : '') }];
    }
    const deadline = parseDeadline(tender.tenderStart);
    if (deadline == null) {
        markers.push({ kind: 'no-deadline', label: 'No deadline', tooltip: 'No submission deadline is known for this tender.' });
    } else {
        const sameYear = deadline.getFullYear() === today.getFullYear();
        const short = deadline.toLocaleDateString('en-GB', sameYear ? SHORT_DATE : FULL_DATE);
        const full = deadline.toLocaleDateString('en-GB', FULL_DATE);
        if (canStillSubmit(tender.tenderStart, today)) {
            markers.push({ kind: 'due', label: `Due ${short}`, tooltip: `Submission deadline: ${full}.` });
        } else {
            markers.push({ kind: 'closed', label: `Closed ${short}`, tooltip: `Submission deadline passed on ${full}.` });
        }
    }
    if (!tender.hasDocuments) {
        markers.push({ kind: 'no-documents', label: 'No documents', tooltip: 'No tender documents are available (yet).' });
    } else if (tender.ndaGated) {
        // The documents exist, but the only one published is the NDA: the
        // summary and the (capped) score come from the SIMAP notice.
        markers.push({ kind: 'nda', label: 'Documents behind NDA',
                       tooltip: 'Only a non-disclosure agreement is published. The summary and the score are estimated '
                              + 'from the SIMAP notice and the score is capped — sign the NDA and request the documents.' });
    } else if (tender.ndaRequired) {
        // The documents are in, but only after signing the NDA: every
        // bidder has to sign one, and what we hold is confidential.
        markers.push({ kind: 'nda-required', label: 'NDA required',
                       tooltip: 'The tender documents were released after signing a non-disclosure agreement. '
                              + 'Every bidder has to sign one; treat the documents as confidential.' });
    }
    // Summary, criteria, vector and score all come from the same pipeline run:
    // no summary means the run has not happened (or failed and is held back);
    // a summary without a score means the scoring step is still owed.
    if (tender.processed === false) {
        markers.push({ kind: 'unprocessed', label: 'Not processed yet',
                       tooltip: 'The documents have not been processed yet — summary, criteria and score appear once they are.' });
    } else if (tender.processed === true && tender.matchingScore == null) {
        markers.push({ kind: 'unscored', label: 'Not scored yet',
                       tooltip: 'Processed, but not scored for your company yet — the daily scoring run fills this in.' });
    }
    return markers;
}

// --- The filter box while searching, hit counts, collapsed summary ---

// The "Status" dropdown: 'open' is the old "Open for Submission" checkbox
// (deadline today or later, no award/abandonment notice); 'closed' = the
// deadline passed without a notice; 'all' = no status filter.
export type StatusFilter = 'open' | 'closed' | 'awarded' | 'abandoned' | 'all';

export const STATUS_FILTER_LABELS: Record<StatusFilter, string> = {
    open: 'Open for submission', closed: 'Closed (deadline passed)', awarded: 'Awarded', abandoned: 'Abandoned', all: 'All',
};

export function passesStatusFilter(status: StatusFilter,
                                   tender: { tenderStart?: string | null; latestPubType?: string | null },
                                   today: Date): boolean {
    const concluded = isConcluded(tender.latestPubType);
    switch (status) {
        case 'open': return !concluded && canStillSubmit(tender.tenderStart, today);
        case 'closed': return !concluded && !canStillSubmit(tender.tenderStart, today);
        case 'awarded': return concluded && tender.latestPubType !== 'abandonment';
        case 'abandoned': return tender.latestPubType === 'abandonment';
        default: return true;
    }
}

export interface ActiveFilters {
    status: StatusFilter;
    hasDocuments: boolean;
    showDismissed: boolean;
    hideLowFit: boolean;
    nda: NdaFilter;
    applyToSearch: boolean; // apply the box (and the tag facets) to search results too
}

// A typed search reaches across all tenders of the project type so a
// searched-for tender never hides behind a filter — unless the user asked
// for the filters to apply to the search as well.
export function filtersBypassed(searchTerm: string, applyToSearch: boolean): boolean {
    return searchTerm.trim() !== '' && !applyToSearch;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

// Under the search field: "12 matches · 20 similar".
export function searchCaption(matches: number, similar: number): string {
    const parts = [plural(matches, 'match', 'matches')];
    if (similar > 0) parts.push(`${similar} similar`);
    return parts.join(' · ');
}

// Next to the list header when not searching: "87 tenders".
export function listCountLabel(count: number): string {
    return plural(count, 'tender', 'tenders');
}

const NDA_FILTER_LABELS: Record<NdaFilter, string | null> = {
    all: null,
    only: 'Only behind NDA',
    required: 'Only NDA required',
    hide: 'Hide behind NDA',
};

const STATUS_SUMMARY: Record<StatusFilter, string> = { open: 'Open', closed: 'Closed', awarded: 'Awarded', abandoned: 'Abandoned', all: '' };

// One line for the collapsed filter box, in the order of the controls.
export function filterSummary(filters: ActiveFilters): string {
    const parts: string[] = [];
    if (filters.status !== 'all') parts.push(STATUS_SUMMARY[filters.status]);
    if (filters.hasDocuments) parts.push('Has documents');
    if (filters.showDismissed) parts.push('Show dismissed');
    if (filters.hideLowFit) parts.push('Hide low fit');
    const nda = NDA_FILTER_LABELS[filters.nda];
    if (nda) parts.push(nda);
    if (filters.applyToSearch) parts.push('Apply to search');
    return parts.length > 0 ? parts.join(' · ') : 'No filters';
}
